import type { PaginationParams } from '@/shared/ui/paginator';
import { UiTableModule } from '@/ui/table/ui-table.module';
import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { groupBy } from '@/utils/toolkit/object/group-by.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import type { primitive } from '@/utils/typing/primitive.type';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, type OnDestroy, type OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, type Observable, of, zip } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import type {
    BusinessIntelligenceDataPoint,
    BusinessIntelligenceDimensionCode,
    BusinessIntelligenceMeasureCode,
    BusinessIntelligenceMemberCode
} from '../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligenceRepresentationComponentConfig } from '../../../interfaces/BusinessIntelligenceRepresentation.interface';
import { SharedUiPaginatorComponent } from '../../../libs/shared/ui/paginator/lib/shared-ui-paginator/shared-ui-paginator.component';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { BusinessIntelligenceRepresentationComponent } from '../business-intelligence-representation-component.class';

export interface BusinessIntelligencePivotTableComponentConfig extends BusinessIntelligenceRepresentationComponentConfig {
    orderedColumnDimensionCodes: BusinessIntelligenceDimensionCode[];
    pivotDimensionCode: BusinessIntelligenceDimensionCode;
    measure: { measureCode: BusinessIntelligenceMeasureCode };
}
const DEFAULT_INIT_PAGINATION = { page: 0, pageSize: 5 };
const MAX_NUMBER_COLUMN = 42;
@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-pivot-table.scss'],
    selector: 'business-intelligence-pivot-table',
    templateUrl: 'business-intelligence-pivot-table.html',
    imports: [UiTableModule, SharedUiPaginatorComponent, LoadingAndErrorComponent, AsyncPipe]
})
export class BusinessIntelligencePivotTableComponent
    extends BusinessIntelligenceRepresentationComponent<BusinessIntelligencePivotTableComponentConfig>
    implements OnInit, OnDestroy
{
    orderedColumnDimensionCodes$!: Observable<BusinessIntelligenceDimensionCode[]>;
    pivotGeneratedColumns$!: Observable<string[]>;

    allLines$!: Observable<
        {
            displayFormat: Record<BusinessIntelligenceDimensionCode, Observable<primitive>>;
            exportFormat: Record<BusinessIntelligenceDimensionCode, Observable<primitive>>;
        }[]
    >;
    displayedLines$!: Observable<Record<BusinessIntelligenceDimensionCode, Observable<primitive>>[]>;
    allColumns$!: Observable<string[]>;
    tooManyColumns: boolean = false;
    totalElements!: number;
    paginationParams$: BehaviorSubject<PaginationParams> = new BehaviorSubject(DEFAULT_INIT_PAGINATION);

    formattingInformation: Record<BusinessIntelligenceDimensionCode, { headerName: string; columnAlignment: string }> = {};
    pivotGeneratedColumnsHeaderNames$!: Observable<Record<string, string>>;
    pivotGeneratedColumnsHeaderNamesForExport$!: Observable<Record<string, string>>;
    formattingPivotCells$!: Observable<string>;

    ngOnInit(): void {
        const validRepresentationDataAndConfig$ = combineLatest([this._representationData$, this._config$]).pipe(
            filter(
                ([representationData, config]) =>
                    representationData && Object.keys(representationData.metadata).length > 0 && isNotNullish(config)
            )
        );
        this.orderedColumnDimensionCodes$ = this._config$.pipe(
            tap(_ => {
                this.paginationParams$.next(DEFAULT_INIT_PAGINATION);
            }),
            map(config => config.orderedColumnDimensionCodes)
        );

        this.pivotGeneratedColumns$ = validRepresentationDataAndConfig$.pipe(
            map(([representationData, config]) => {
                const pivotDimensionCode = config.pivotDimensionCode;
                return Array.from(
                    groupBy(representationData.data, dataPoint =>
                        this.pivotValueToColumnCode(dataPoint.dimensions[pivotDimensionCode])
                    ).keys()
                );
            }),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        this.allColumns$ = zip(validRepresentationDataAndConfig$, this.pivotGeneratedColumns$).pipe(
            map(([[_, config], pivotGeneratedColumns]) => [...config.orderedColumnDimensionCodes, ...pivotGeneratedColumns])
        );

        this.allColumns$.pipe(untilDestroyed(this)).subscribe(allColumns => (this.tooManyColumns = allColumns.length > MAX_NUMBER_COLUMN));

        this.formattingPivotCells$ = validRepresentationDataAndConfig$.pipe(
            filter(isNotNullish),
            switchMap(([representationData, config]) => {
                const measureCode = config.measure.measureCode;
                return representationData.metadata[measureCode].valuesFormatting.groupedText.getAlignment$();
            })
        );

        this.allLines$ = validRepresentationDataAndConfig$.pipe(
            map(([representationData, config]) => {
                const dataPointGroupedByNonPivotColumnsCombination = Array.from(
                    groupBy(representationData.data, this.keyGenerator(config.orderedColumnDimensionCodes)).entries()
                );

                const lines = dataPointGroupedByNonPivotColumnsCombination.map(dataForOnePivotLine => {
                    const displayFormattedLine: Record<any, Observable<primitive>> = {};
                    const exportFormattedLine: Record<any, Observable<primitive>> = {};
                    const dataPointsForOnePivotLine: BusinessIntelligenceDataPoint[] = dataForOnePivotLine[1];
                    for (const dimensionCode of config.orderedColumnDimensionCodes) {
                        displayFormattedLine[dimensionCode] = representationData.metadata[
                            dimensionCode
                        ].valuesFormatting.groupedText.formatAsGroupedText$(dataPointsForOnePivotLine[0].dimensions[dimensionCode]);
                        exportFormattedLine[dimensionCode] = representationData.metadata[
                            dimensionCode
                        ].valuesFormatting.exportData.formatAsExportData$(dataPointsForOnePivotLine[0].dimensions[dimensionCode]);
                    }
                    for (const dataPoint of dataPointsForOnePivotLine) {
                        const pivotValueColumnCode = this.pivotValueToColumnCode(dataPoint.dimensions[config.pivotDimensionCode]);
                        displayFormattedLine[pivotValueColumnCode] = representationData.metadata[
                            config.measure.measureCode
                        ].valuesFormatting.groupedText.formatAsGroupedText$(dataPoint.measures[config.measure.measureCode]);
                        exportFormattedLine[pivotValueColumnCode] = representationData.metadata[
                            config.measure.measureCode
                        ].valuesFormatting.exportData.formatAsExportData$(dataPoint.measures[config.measure.measureCode]);
                    }
                    return {
                        displayFormat: displayFormattedLine,
                        exportFormat: exportFormattedLine
                    };
                });

                this.totalElements = lines.length;
                return lines;
            })
        );

        this.displayedLines$ = combineLatest([
            this.paginationParams$.pipe(filter(isNotNullish)),
            this.allLines$.pipe(filter(isNotNullish))
        ]).pipe(
            map(([paginationParams, allLines]) => {
                const firstIndex = paginationParams.page * paginationParams.pageSize;
                const lastIndex = (paginationParams.page + 1) * paginationParams.pageSize;
                return allLines.slice(firstIndex, lastIndex).map(line => line.displayFormat);
            })
        );

        this._representationData$
            .pipe(
                filter(isNotNullish),
                map(representationData => representationData.metadata),
                distinctUntilChanged(),
                switchMap(representationDataMetadata =>
                    Object.keys(representationDataMetadata).map(memberCode =>
                        combineLatest([
                            of(memberCode),
                            representationDataMetadata[memberCode].getName$(),
                            representationDataMetadata[memberCode].valuesFormatting.groupedText.getRepeatedInformation$(),
                            representationDataMetadata[memberCode].valuesFormatting.groupedText.getAlignment$()
                        ])
                    )
                ),
                untilDestroyed(this)
            )
            .subscribe(formattingDataArray => {
                formattingDataArray.forEach(([memberCode, formattedName, repeatedInformation, columnAlignment]) => {
                    this.formattingInformation[memberCode as BusinessIntelligenceDimensionCode] = {
                        headerName: repeatedInformation ? `${formattedName} (${repeatedInformation})` : formattedName,
                        columnAlignment
                    };
                });
            });

        this.pivotGeneratedColumnsHeaderNames$ = validRepresentationDataAndConfig$.pipe(
            switchMap(([representationData, config]) => {
                const representationDataMetadata = representationData.metadata;
                const measureCode = config.measure.measureCode;
                const pivotDimensionCode = config.pivotDimensionCode;
                const pivotValues = Array.from(
                    groupBy(representationData.data, dataPoint => dataPoint.dimensions[pivotDimensionCode]).keys()
                );

                return combineLatest([
                    representationDataMetadata[measureCode].valuesFormatting.groupedText.getRepeatedInformation$(),
                    ...pivotValues.map(value =>
                        representationDataMetadata[pivotDimensionCode].valuesFormatting.formatAsStandaloneText$(value)
                    )
                ]).pipe(
                    map(([repeatedInformation, ...formattedValues]) =>
                        formattedValues.reduce(
                            (acc, value, index) => {
                                acc[this.pivotValueToColumnCode(pivotValues[index])] = repeatedInformation
                                    ? `${value} (${repeatedInformation})`
                                    : value;
                                return acc;
                            },
                            {} as Record<string, string>
                        )
                    )
                );
            }),
            untilDestroyed(this)
        );

        this.pivotGeneratedColumnsHeaderNamesForExport$ = validRepresentationDataAndConfig$.pipe(
            switchMap(([representationData, config]) => {
                const representationDataMetadata = representationData.metadata;
                const measureCode = config.measure.measureCode;
                const pivotDimensionCode = config.pivotDimensionCode;
                const pivotValues = Array.from(
                    groupBy(representationData.data, dataPoint => dataPoint.dimensions[pivotDimensionCode]).keys()
                );

                return combineLatest([
                    representationDataMetadata[measureCode].valuesFormatting.exportData.getRepeatedInformation$(),
                    ...pivotValues.map(value =>
                        representationDataMetadata[pivotDimensionCode].valuesFormatting.formatAsStandaloneText$(value)
                    )
                ]).pipe(
                    map(([repeatedInformation, ...formattedValues]) =>
                        formattedValues.reduce(
                            (acc, value, index) => {
                                acc[this.pivotValueToColumnCode(pivotValues[index])] = repeatedInformation
                                    ? `${value} (${repeatedInformation})`
                                    : value;
                                return acc;
                            },
                            {} as Record<string, string>
                        )
                    )
                );
            }),
            untilDestroyed(this)
        );
    }

    ngOnDestroy(): void {}

    getExportMembers(): BusinessIntelligenceMemberCode[] {
        return [];
    }

    private pivotValueToColumnCode(value: primitive | null | undefined): string {
        return isNotNullish(value) && value.toString() !== '' ? value.toString() : ' ';
    }

    keyGenerator(listDimensionCode: BusinessIntelligenceDimensionCode[]): (dataPoint: BusinessIntelligenceDataPoint) => string {
        return dataPoint => {
            const groupingElement: any = {};
            for (const dimensionCode of listDimensionCode) {
                groupingElement[dimensionCode] = dataPoint.dimensions[dimensionCode];
            }
            return JSON.stringify(groupingElement);
        };
    }

    setPagination(event: PaginationParams) {
        this.paginationParams$.next(event);
    }

    exportAsCsv$(): Observable<{ data: any[]; columns: { header: string; value: (line: any) => primitive }[] }> {
        return combineLatest([
            this.orderedColumnDimensionCodes$,
            this.allLines$.pipe(switchMap(combineLatestDeepObject)),
            this.pivotGeneratedColumnsHeaderNamesForExport$
        ]).pipe(
            map(([orderedColumnDimensionCodes, allLines, pivotGeneratedColumnsHeaderNames]) => {
                const columns = [
                    ...orderedColumnDimensionCodes.map(memberCode => ({
                        code: memberCode,
                        header: this.formattingInformation[memberCode].headerName
                    })),
                    ...Object.keys(pivotGeneratedColumnsHeaderNames).map(code => ({
                        code,
                        header: pivotGeneratedColumnsHeaderNames[code]
                    }))
                ].map((column, index) => {
                    return {
                        ...column,
                        value: (line: any) => line[index]
                    };
                });
                const data = allLines.map((line: any) => {
                    return columns.reduce((lineData, columnName, columnIndex) => {
                        (lineData as any)[columnIndex] = line.exportFormat[columnName.code];
                        return lineData;
                    }, {});
                });
                return { data, columns };
            })
        );
    }
}
